<template>
    <div class="report__page report__setup">
        <div class="repeat__banner">
            <div class="repeat__wrapper">
                <span>Report Frequency</span>
                <span class="p-d-flex p-ai-center p-jc-md-between" style="gap: 0.75rem; width: fit-content;">
                    <small style="margin-right: 2rem;">Repeat</small>
                    <InputSwitch v-model="repeatReport" @change="(event) => {toggleRepeatable(), updateSchedulingStore()}"/>
                    <span>{{ repeatReportText }}</span>
                </span>
            </div>
            <hr>
        </div>

        <div id="repeat__details">
            <div class="report__detail--wrapper">
                <span :class="(repeatReport) ? '' : 'disabled__label'">Repeat Every</span>
                <!-- <span :class="(repeatReport) ? '' : 'disabled__label'">Frequency</span> -->
                <InputText @input="updateSchedulingStore()" :disabled="!repeatReport" v-model="intervalFrequency" class="frequency__input" type="number" />
            </div>

            <div class="report__detail--wrapper">
                <Dropdown @change="updateSchedulingStore()" :disabled="!repeatReport" v-model="intervalType" :options="intervalOptions" optionLabel="label"></Dropdown>
            </div>

            <div v-if="intervalType.value === 'monthly'" class="report__detail--wrapper">
                <span :class="(repeatReport) ? '' : 'disabled__label'">on</span>
                <Dropdown @change="updateSchedulingStore()" :disabled="!repeatReport" v-model="monthlyDate" :options="dateOptions" optionLabel="label"></Dropdown>
            </div>
            
            <div v-if="intervalType.value === 'weekly'" class="report__detail--wrapper">
                <span :class="(repeatReport) ? '' : 'disabled__label'">on</span>
                <div class="weekday__button--wrapper">
                    <button 
                    v-for="weekday in weekdays" 
                    :key="weekday.value"
                    :disabled="!repeatReport" 
                    :class="(selectedWeekday.some(day => day.value === weekday.value)) ? 'selected' : ''" 
                    class="weekday__button" 
                    @click.prevent="(event) => {toggleSelectedWeekday(weekday.value); updateSchedulingStore()}"
                    >
                        {{ weekday.label }}
                    </button>
                </div>
            </div>

            <div class="report__detail--wrapper">
                <span :class="(repeatReport) ? '' : 'disabled__label'">at</span>
                <Dropdown @change="updateSchedulingStore()" :disabled="!repeatReport" class="time__multiselect" v-model="intervalTime" :options="timeOptions" optionLabel="label" placeholder="Select Time"></Dropdown>
            </div>
        </div>

        <div id="report__format">
            <span>Format Required:</span>
            <div>
                <RadioButton @change="updateSchedulingStore()" id="reportFormatCSV" name="reportFormatCSV" value="csv" v-model="reportFormat"></RadioButton>
                <label for="reportFormatCSV">CSV</label>
            </div>
            <div>
                <RadioButton @change="updateSchedulingStore()" id="reportFormatPDF" name="reportFormatPDF" value="pdf" v-model="reportFormat"></RadioButton>
                <label for="reportFormatPDF">PDF</label>
            </div>
            <div>
                <RadioButton @change="updateSchedulingStore()" id="reportFormatExcel" name="reportFormatExcel" value="xlxs" v-model="reportFormat"></RadioButton>
                <label for="reportFormatExcel">Excel</label>
            </div>
        </div>

        <div id="report__emails">
            <div>
                <label for="emailMain">Send to:</label>
                <Chips @add="updateSchedulingStore()" @remove="updateSchedulingStore()" name="emailMain" v-model="emailMain" separator="," addOnBlur />
            </div>

            <div>
                <label for="emailCC">CC:</label>
                <Chips @add="updateSchedulingStore()" @remove="updateSchedulingStore()" name="emailCC" v-model="emailCC" separator="," addOnBlur />
            </div>

            <div class="email__test_send">
                <label for="testReportEmail">Send a test report</label>
                <div>
                    <Chips @add="updateSchedulingStore()" @remove="updateSchedulingStore()" name="testReportEmail" v-model="testReportEmail" separator="," addOnBlur />
                    <Button :disabled="!testReportEmail.length || testReportSending" :icon="testReportSending ? 'pi pi-spin pi-spinner' : ''" iconPos="right" :label="testReportSending ? 'Sending Test...' : 'Send Test'" @click="sendTestReport"/>
                </div>
            </div>


            <div class="email__custom_text">
                <label for="testReportEmail" class="p-d-flex p-ai-center p-jc-md-between" style="gap: 0.75rem; width: fit-content;">
                    <small style="margin-right: 2rem;">Report Body Custom text</small>
                    <InputSwitch @change="(event) => {updateSchedulingStore(); toggleCustomtext()}" v-model="useCustomText" />
                    <span>{{ reportCustomText }}</span>
                </label>
                <InputText @input="updateSchedulingStore()" :disabled="!useCustomText" name="reportCustomInput" v-model="reportCustomInput" type="text" maxlength="300" />
            </div>
        </div>
    </div>
</template>

<script>
import {
    DATE_OPTIONS,
    WEEKDAYS,
    INTERVAL_OPTIONS,
    TIME_OPTIONS
} from '../../constants/reportFrequencyOptions.js'

export default {
    data() {
        return {
            repeatReportText: 'No',
            repeatReport: false,
            intervalFrequency: '', // stores as a string if removed, so should start as empty string
            intervalType: { label: 'Day(s)', value: 'daily' },
            intervalOptions: INTERVAL_OPTIONS,
            intervalTime: { label: '00:00', value: '00:00' },
            timeOptions: TIME_OPTIONS,
            monthlyDate: { label: '1st', value: '1' },
            dateOptions: DATE_OPTIONS,
            selectedWeekday: [],
            weekdays: WEEKDAYS,
            reportFormat: 'csv',
            emailMain: [],
            emailCC: [],
            testReportEmail: [],
            useCustomText: false,
            reportCustomText: 'No',
            reportCustomInput: '',
            testReportSending: false
        }
    },

    mounted() {
        const schedulingDetails = this.$store.getters['createCustomReport/getSchedulingDetails'];
        
        if (schedulingDetails) {
            this.repeatReport = schedulingDetails.repeatReport || false;
            this.repeatReportText = this.repeatReport ? 'Yes' : 'No';
            
            this.intervalFrequency = schedulingDetails.reportInterval || '';
            this.intervalType = this.intervalOptions.find(opt => opt.value === schedulingDetails.reportScheduleType) || { label: 'Day(s)', value: 'daily' };
            
            this.intervalTime = schedulingDetails.reportTime 
                ? { label: schedulingDetails.reportTime[0], value: schedulingDetails.reportTime[0] }
                : { label: '00:00', value: '00:00' };
            
            this.monthlyDate = this.dateOptions.find(opt => opt.value === schedulingDetails.monthlyDate) || { label: '1st', value: '1' };
            
            this.selectedWeekday = schedulingDetails.weeklyDays.map(day => ({label: day.label, value: day.value})) || [];
            
            this.reportFormat = schedulingDetails.reportFormat || 'csv';
            
            this.emailMain = schedulingDetails.sendTo || [];
            this.emailCC = schedulingDetails.ccTo || [];
            
            this.useCustomText = schedulingDetails.useCustomtext || false;
            this.reportCustomText = this.useCustomText ? 'Yes' : 'No';
            this.reportCustomInput = schedulingDetails.reportCustomText || '';
        }
    },

    watch: {
        repeatReport(newVal) {
            this.repeatReportText = newVal ? 'Yes' : 'No';
        },
        useCustomText(newVal) {
            this.reportCustomText = newVal ? 'Yes' : 'No';
        }
    },

    computed: {
    },

    methods: {

        updateSchedulingStore() {
            const intervalTime = [this.intervalTime]
            this.$store.dispatch('createCustomReport/setSchedulingDetails', {
                repeatReport: this.repeatReport, // boolean for repeating report
                reportScheduleType: this.intervalType.value, // daily, weekly, monthly
                reportInterval: this.intervalFrequency, // numberical interval of report i.e. every /2/ days, every /3/ months
                reportTime: intervalTime.map(x => x.value), // hour of day to send report
                weeklyDays: this.selectedWeekday, // used for weekly report type, sunday, monday etc
                monthlyDate: this.monthlyDate.value, // can be a number or 'last'
                reportFormat: this.reportFormat, // csv, xlxs, pdf
                useCustomtext: this.useCustomText,
                reportCustomText: this.reportCustomInput, // Report Body Custom text
                sendTo: this.emailMain,
                ccTo: this.emailCC,
            })
        },

        toggleSelectedWeekday(weekdayValue) {
            const thisWeekday = this.weekdays.find(x => x.value == weekdayValue)

            if (this.selectedWeekday.some(day => day.value === weekdayValue)) {
                this.selectedWeekday = this.selectedWeekday.filter(day => day.value !== weekdayValue)
            } else {
                this.selectedWeekday.push(thisWeekday)
            }
        },
        toggleRepeatable() {
            (this.repeatReport)
                ? this.repeatReportText = 'Yes'
                : this.repeatReportText = 'No'
            },
        toggleCustomtext() {
            (this.useCustomText)
                ? this.reportCustomText = 'Yes'
                : this.reportCustomText = 'No'
        },

        sendTestReport() {
            this.testReportSending = true
            this.$emit('send-test-report', this.testReportEmail)
            // this is a temporary fix to show the loading state
            setTimeout(() => {
                this.testReportSending = false
            }, 3000)
        }
    }
}
</script>

<style lang="scss">
.repeat__banner {
    padding-inline: 1.5rem;
    padding-top: 1.5rem;
}

.repeat__wrapper {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
}

#repeat__details {
    display: flex;
    align-items: center;
    gap: 1.5rem;
    padding-inline: 1.5rem;
    flex-wrap: wrap;
}

.report__detail--wrapper {
    display: flex;
    align-items: center;
    gap: 1.5rem;

    .disabled__label {
        opacity: 0.6;
    }
}

.frequency__input {
    width: 3rem;
}

.time__multiselect {
    width: 12rem;
    max-width: 12rem;
    // overflow-x: hidden;
}

.weekday__button {
    all: revert;
    cursor: pointer;
    border: none;
    width: 2.5rem;
    height: 2.5rem;
    background: #eee;
    color: black;
    border-radius: 100%;
    transition: all 0.2s ease;
    display: flex;
    justify-content: center;
    align-items: center;

    &:hover {
        background: #eaeaea;
    }

    &--wrapper {
        display: flex;
        gap: 0.5rem;
        flex-wrap: wrap;
    }

    &.selected {
        background: #2196F3;
        color: white;
    }
}

#report__format {
    display: flex;
    gap: 1.5rem;
    padding-inline: 1.5rem;
    margin-top: 1.5rem;

    >div {
        display: flex;
        gap: 0.25rem;
    }
}

.report__setup {
    display: grid;
    gap: 1.5rem;
}


#report__emails {
    display: grid;
    gap: 1.5rem;
    padding-inline: 1.5rem;
    margin-bottom: 3rem;

    >div {
        display: grid;
        gap: 0.5rem;
        overflow-x: scroll;
        
        /* Custom scrollbar styling */
        &::-webkit-scrollbar {
            height: 4px;
        }
        
        &::-webkit-scrollbar-track {
            background: #fff;
            border-radius: 3px;
        }
        
        &::-webkit-scrollbar-thumb {
            background: #888;
            border-radius: 3px;
            
            &:hover {
                background: #555;
            }
        }
        
        >label {
            font-weight: bold;
        }
        
        >.p-chips,
        >.p-chips ul {
            width: 100%;
            max-width: 100%;
        }
    }

    .email__test_send, .email__custom_text {
        margin-top: 1.5rem;

        >div {
            width: 100%;
            display: flex;
            gap: 0.5rem;

            >.p-chips,
            >.p-chips ul {
                flex-grow: 1;
            }

            button {
                background-color: black;
            }
        }
    }

    .email__custom_text {
        >label {
            font-weight: normal;
        }
    }

    .p-chips-multiple-container {
        flex-wrap: wrap;
        row-gap: 0.25rem;
    }

}
</style>